import React, { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Button, Stack, Typography } from '@mui/material';
import { toast } from '../../ui/toast';
import { AUTH_KEYS, NS } from '../../i18n/constants/keys';
import { AnimatedLoginSequence } from '../../assets/svgs/AnimateLogin';
import { TextInput } from '../../ui/input/text_input/text_input';
import {
  createUser,
  getUser,
  loginUser,
} from '../../utils/api_service/user_api';
import { RootState } from '../../utils/redux_store/store';
import { FormInput } from '../../utils/types/login_register_types';
import { AuthenticationUiLayout } from './AuthenticationUILayout';
import { CompanyLogoBadge } from './CompanyLogoBadge';
import { validate, validateForm } from './validation/validation_rules';

type UserLandingParams = {
  token: string;
  email: string;
  firstname: string;
  lastname: string;
};

export const UserLanding: React.FC = () => {
  const history = useHistory();
  const { t } = useTranslation(NS.auth);
  const { token, email, firstname, lastname } = useParams<UserLandingParams>();
  const [landingInputs, setLandingInputs] = useState<FormInput>({
    email: email,
    firstname: firstname,
    lastname: lastname,
    password: '',
    confirmPassword: '',
    errors: {
      emailError: '',
      passwordError: '',
      confirmPasswordError: '',
    },
  });

  useEffect(() => {
    localStorage.setItem('token', token);
    getUser()
      .then((response) => {
        if (response.ok) {
          return response.json();
        } else {
          throw new Error('Failed to fetch user data');
        }
      })
      .then((data) => {
        if (data && data.isVerified) {
          localStorage.clear();
          history.push(`/login/${email}`);
        }
      })
      .catch((error) => {
        toast.error(error.message, { position: 'bottom-center' });
        history.push('/login');
      });
  }, [token, email, history]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    let errors = validate(event.target, landingInputs);

    setLandingInputs({
      ...landingInputs,
      [event.target.name]: event.target.value,
      errors: errors,
    });
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    let isSubmittedFormValid = validateForm(landingInputs);
    if (!isSubmittedFormValid) {
      toast.error(t(AUTH_KEYS.login.invalidDetails), {
        position: 'bottom-center',
      });
      return;
    }

    try {
      localStorage.setItem('token', token);
      const passwordChangeResponse = await createUser(landingInputs.password);
      if (!passwordChangeResponse.ok) {
        throw new Error('Password change failed');
      }

      const loginResponse = await loginUser(landingInputs);
      if (!loginResponse.success) {
        throw new Error('Entered email or password is incorrect or unverified');
      }

      localStorage.setItem('token', loginResponse.accessToken);
      history.push(`/terms-conditions/${loginResponse.accessToken}`);
    } catch (error: any) {
      toast.error(error.message, { position: 'bottom-center' });
    }
  };

  const companyLogo = useSelector((state: RootState) => state.logo.companyLogo);
  const companyName = useSelector((state: RootState) => state.logo.companyName);

  return (
    <AuthenticationUiLayout leftContent={<AnimatedLoginSequence />}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%', gap: 2.5 }}>
        <CompanyLogoBadge
          logoUrl={companyLogo}
          companyName={companyName}
          altText='HiAcuity Logo'
        />
        <Typography
          variant="h4"
          sx={{
            fontSize: { xs: '1.5rem', sm: '1.875rem' },
            fontWeight: 800,
            color: '#0f172a',
            letterSpacing: '-0.02em',
          }}
        >
          {t(AUTH_KEYS.userLanding.signUp)}
        </Typography>
      </Box>

      <Box
        component="form"
        onSubmit={handleSubmit}
        data-testid="user-landing-form"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          mt: 3,
          width: '100%',
        }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <TextInput
            type="text"
            label={t(AUTH_KEYS.register.firstname)}
            name="firstname"
            variant="pill"
            value={landingInputs.firstname || ''}
            onChange={handleChange}
            required
            disabled
          />
          <TextInput
            type="text"
            label={t(AUTH_KEYS.register.lastname)}
            name="lastname"
            variant="pill"
            value={landingInputs.lastname || ''}
            onChange={handleChange}
            required
            disabled
          />
        </Stack>

        <TextInput
          type="email"
          label={t(AUTH_KEYS.register.email)}
          data-testid="email"
          name="email"
          variant="pill"
          value={landingInputs.email}
          onChange={handleChange}
          disabled
        />

        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Typography sx={{ fontSize: '0.8125rem', fontWeight: 700, color: '#334155' }}>
            {t(AUTH_KEYS.userLanding.setPassword)}
          </Typography>

          <TextInput
            type="password"
            label={t(AUTH_KEYS.register.password)}
            data-testid="password"
            name="password"
            variant="pill"
            placeholder="Create password"
            value={landingInputs.password}
            onChange={handleChange}
            required
            error={Boolean(landingInputs.errors.passwordError)}
            helperText={landingInputs.errors.passwordError || undefined}
          />

          <TextInput
            type="password"
            label={t(AUTH_KEYS.changePassword.confirmPassword)}
            data-testid="confirmPassword"
            name="confirmPassword"
            variant="pill"
            placeholder="Confirm password"
            value={landingInputs.confirmPassword || ''}
            onChange={handleChange}
            required
            error={Boolean(landingInputs.errors.confirmPasswordError)}
            helperText={landingInputs.errors.confirmPasswordError || undefined}
          />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}>
          <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#64748b' }}>
            {t(AUTH_KEYS.userLanding.alreadyHaveAccount)}{' '}
            <Box
              component="span"
              onClick={() => history.push('/login')}
              sx={{
                color: '#0f172a',
                fontWeight: 700,
                cursor: 'pointer',
                ml: 0.5,
                '&:hover': {
                  color: '#059669',
                  textDecoration: 'underline',
                },
              }}
            >
              {t(AUTH_KEYS.userLanding.signIn)}
            </Box>
          </Typography>
        </Box>

        <Button
          fullWidth
          type="submit"
          variant="contained"
          sx={{
            mt: 1,
            py: 1.5,
            px: 3,
            borderRadius: '9999px',
            bgcolor: '#050508',
            color: '#ffffff',
            fontSize: '0.875rem',
            fontWeight: 600,
            textTransform: 'none',
            boxShadow: '0 4px 14px rgba(0, 0, 0, 0.16)',
            '&:hover': {
              bgcolor: '#1e293b',
              boxShadow: '0 6px 20px rgba(0, 0, 0, 0.22)',
            },
          }}
        >
          {t(AUTH_KEYS.userLanding.createAccount)}
        </Button>
      </Box>
    </AuthenticationUiLayout>
  );
};
